<script lang="ts" setup>
  import { useMutation } from '@tanstack/vue-query'

  import { useUsersService } from '~/core/services'
  import type { FetchErrorObj, Nullable } from '~/core/types'
  import type { PostUsersDTO, User } from '~/core/types/users'

  import 'vue-tel-input/vue-tel-input.css'

  import ConfirmMail from './ConfirmMail.vue'

  const emit = defineEmits<{
    (e: 'login'): void
  }>()
  const registerStore = useRegisterStore()
  const { postUser } = useUsersService()
  const { payload } = storeToRefs(registerStore)

  const { t } = useI18n()

  const step = ref(1)
  const hasConsent = ref(false)

  const {
    mutate: register,
    isPending: isPendingRegister,
    error,
  } = useMutation<Nullable<FetchResult<User>>, FetchErrorObj<PostUsersDTO>, PostUsersDTO>({
    mutationFn: (p: PostUsersDTO) => postUser(p),
    onSuccess: () => {
      step.value = 3
    },
    onError(error) {
      const step1Keys: (keyof PostUsersDTO)[] = ['password', 'password_confirmation', 'email']
      const step2Keys: (keyof PostUsersDTO)[] = ['first_name', 'last_name', 'phone']
      const errorKeys = error.errors ? Object.keys(error.errors) as (keyof PostUsersDTO)[] : []
      if (errorKeys.some((k) => step1Keys.includes(k))) {
        step.value = 1
      } else if (errorKeys.some((k) => step2Keys.includes(k))) {
        step.value = 2
      }
    },
  })
  const consentLabel = `Je consens à la collecte de mes données par BOOKANDGOLF à des fins de Gestion de ma réservation / demande pour la durée nécessaire au traitement conformément à la <a href="/privacy-policy">Politique de confidentialité</a>. L'exercice de vos droits est possible à l'adresse <a href="mailto:dpo@groupeduval.com">dpo@groupeduval.com</a>`
  function login() {
    emit('login')
  }

  function onSubmit() {
    if (step.value === 1) {
      step.value = 2
    } else {
      register(payload.value)
    }
  }
</script>
<template>
  <div class="c-register-form">
    <template v-if="[1, 2].includes(step)">
      <div class="title">
        <SvgIcon
          icon="account"
          :width="22"
          :height="22"
          :desktop-height="32"
          :desktop-width="32"
        />
        <span>{{ t("authentication.register-title") }}</span>
      </div>
      <div class="sub-title">
        <span>{{ t(`authentication.register-sub-title-${step}`) }}</span>
      </div>
      <form
        class="form"
        @submit.prevent="onSubmit"
      >
        <!-- <InputErrors
          v-if="error"
          :errors="[error.message]"
        /> -->
        <fieldset
          v-if="step === 1"
          class="form-top"
        >
          <InputBase
            v-model="payload.email"
            class="input"
            type="email"
            name="email"
            :label="t('common.email')"
            placeholder=""
            required
            :errors="error?.errors?.email"
          />
          <InputBase
            v-model="payload.password"
            class="input"
            type="password"
            name="password"
            :label="t('common.password')"
            required
            :errors="error?.errors?.password"
          />
          <span
            v-if="!error?.errors?.password"
            class="helper"
          >{{ t("authentication.rgpd") }}</span>
          <InputBase
            v-model="payload.password_confirmation"
            class="input"
            type="password"
            name="password-confirmation"
            :label="t('authentication.password-confirmation')"
            required
            :errors="error?.errors?.password_confirmation"
          />
        </fieldset>
        <fieldset
          v-else-if="step === 2"
          class="form-top"
        >
          <InputBase
            v-model="payload.last_name"
            class="input"
            type="text"
            name="nom"
            rules="required"
            :label="t('authentication.last-name')"
            :placeholder="t('authentication.last-name')"
            required
            :errors="error?.errors?.last_name"
          />
          <InputBase
            v-model="payload.first_name"
            class="input"
            type="text"
            name="prénom"
            rules="required"
            :label="t('authentication.first-name')"
            :placeholder="t('authentication.first-name')"
            required
            :errors="error?.errors?.first_name"
          />
          <InputPhone
            v-model="payload.phone"
            required
            name="phone"
            :errors="error?.errors?.phone"
          />
        </fieldset>
        <p
          v-if="step === 2"
          class="rgpd"
        >
          « Conformément aux dispositions de l’article L. 223-2 du Code de la
          consommation, le Client est informé de son droit de s’inscrire sur la liste d’opposition au démarchage
          téléphonique disponible sur le site www.bloctel.gouv.fr. »
        </p>
        <InputCheckbox
            v-model="hasConsent"
            class="consent-checkbox"
            name="register-consent"
            :label="consentLabel"
            required
        />
        <ButtonAndLink
          class="form-bottom"
          :disabled="isPendingRegister"
          button-type="submit"
          @link-click="login"
        >
          <template #button>
            {{ step === 1 ? t("common.next") : t("authentication.register") }}
          </template>
          <template #caption>
            {{ t("authentication.got-account") }}
          </template>
          <template #link>
            {{ t("authentication.login") }}
          </template>
        </ButtonAndLink>
      </form>
    </template>
    <ConfirmMail v-else />
  </div>
</template>
<style scoped>

.c-register-form {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  padding: 1.5em;
  overflow-y: auto;

  .errors {
    margin-top: 0.25em;
  }

  .title {
    display: flex;
    width: 100%;
    flex: 0 0 auto;
    flex-direction: row;
    align-items: center;

    span {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1.125em;
      font-weight: bold;
    }

    .c-icon.account {
      margin-right: 1.25em;
      color: var(--color-primary);
    }
  }

  .sub-title {
    flex: 0 0 auto;
    align-self: flex-start;
    margin-top: 1em;

    span {
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 1em;
      font-weight: 600;
      letter-spacing: normal;
      line-height: 1.38;
      opacity: 0.8;
    }
  }

  .form {
    display: flex;
    width: 100%;
    flex: 1 0 auto;
    flex-direction: column;
    justify-content: space-between;
    margin-top: 2em;

    label {
      display: flex;
      margin-top: 1em;
      margin-bottom: 0.5rem;
      color: var(--color-secondary);
      font-family: Open-Sans, sans-serif;
      font-size: 0.875em;
      opacity: 0.6;

      &::first-letter {
        text-transform: uppercase;
      }
    }

    .input {
      margin-top: 1em;
    }

    .c-checkbox {
      margin-top: 1em;
    }

    .consent-checkbox {
      :deep(.label-txt) {
        color: var(--color-grey-light);
      }
    }

    .form-top {
      .helper {
        color: var(--color-primary);
        font-family: Open-Sans, sans-serif;
        font-size: 0.75em;
        font-weight: normal;
      }
    }

    .rgpd {
      margin-top: 1rem;
      color: var(--color-grey-light);
      font-family: Open-Sans, sans-serif;
      font-size: 0.75em;
      font-weight: normal;
    }

    .form-bottom {
      display: flex;
      flex-direction: column;
      align-items: center;
      margin-top: 3em;

      .button {
        width: 100%;
        align-items: center;
        font-size: 1.25em;
        text-transform: uppercase;
      }

      .caption {
        margin-top: 1.5em;

        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 0.875em;
          opacity: 0.6;
        }
      }

      .link-underline {
        margin-top: 1.25em;
        letter-spacing: 1.5px;
        text-decoration: underline;
        text-transform: uppercase;

        span {
          color: var(--color-secondary);
          font-family: Open-Sans, sans-serif;
          font-size: 1.125em;
        }
      }
    }
  }

}

@media screen and (width >= 1024px) {
  .c-register-form {
    padding: 0;

    .title {
      span {
        color: var(--color-secondary);
        font-family: Bariol, sans-serif;
        font-size: 2.5em;
        font-weight: bold;
      }

      .c-icon.account {
        margin-right: 1.25rem;
      }
    }

    .sub-title {
      span {
        color: var(--color-secondary);
        font-family: Open-Sans, sans-serif;
        font-size: 1.25em;
        font-weight: 600;
      }
    }

    .form {
      .form-bottom {
        .button {
          font-size: 1.5em;
        }

        .caption {
          span {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1em;
          }
        }

        .link-underline {
          span {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1.25em;
          }
        }
      }
    }
  }
}
</style>
